import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ModalElevated, Icon } from '../../../../ui';
import { useLayoutOrganizer } from '../../hooks';
import { useCourseTheme } from '../../../course/hooks';
import { getUserThemeColor } from '../../../../ui/theme/colors';
import { notifySuccess, notifyError } from '../../../../services/notifications';
import LayoutOrganizerContent from './LayoutOrganizerContent';

/**
 * ModuleLayoutOrganizerModal - Modal for reorganizing module layout via drag-and-drop
 * Saves immediately on drop (no save/cancel buttons needed)
 */
const ModuleLayoutOrganizerModal = ({ open, onClose, pageRows, pageId }) => {
  const { t } = useTranslation();

  const courseTheme = useCourseTheme();
  const themeColor = useMemo(() => getUserThemeColor(courseTheme?.color), [courseTheme]);

  const handleSaveSuccess = useCallback(() => {
    notifySuccess(t('editCourse.layoutSaved'));
  }, [t]);

  const handleSaveError = useCallback(
    (error) => {
      notifyError(t('editCourse.layoutSaveFailed'));
      console.error('Failed to save layout:', error);
    },
    [t],
  );

  const { localRows, isSaving, handleDrop } = useLayoutOrganizer({
    pageRows,
    pageId,
    onSaveSuccess: handleSaveSuccess,
    onSaveError: handleSaveError,
  });

  return (
    <ModalElevated
      open={open}
      onClose={onClose}
      title={t('editCourse.layoutOrganizer')}
      subtitle={t('editCourse.layoutOrganizerDescription')}
      icon={Icon.EditLayout}
      width='650px'
      maxWidth='md'
      showActions={false}
    >
      <DndProvider backend={HTML5Backend}>
        <LayoutOrganizerContent localRows={localRows} onDrop={handleDrop} themeColor={themeColor} isSaving={isSaving} />
      </DndProvider>
    </ModalElevated>
  );
};

export default ModuleLayoutOrganizerModal;
